import { Graphics, RenderTexture, Texture, TilingSprite, Application } from 'pixi.js-legacy';
import { CONFIG } from '../../config';

type BackgroundStyle = 'stripe' | 'diamond' | 'triangle' | 'sine' | 'hexagon' | 'grid';

/**
 * 스피드 라인 텍스쳐 캐시
 * 6개 패턴 × 2방향 (landscape/portrait) = 12개 텍스쳐 캐싱
 * TilingSprite로 사용하여 매 프레임 Graphics 렌더링 제거
 */
export class SpeedLineTextureCache {
    private static instance: SpeedLineTextureCache | null = null;
    private textures: Map<string, Texture> = new Map();
    private app: Application | null = null;
    private initialized: boolean = false;

    // 패턴별 타일 크기 (반복 단위의 정확한 배수)
    // 중요: 높이는 SPEED_LINE_SPACING(120)의 배수여야 스크롤이 자연스러움
    private readonly TILE_SIZES: Record<BackgroundStyle, { width: number; height: number }> = {
        stripe: { width: 240, height: 240 },      // lineSpacing(120) × 2
        diamond: { width: 240, height: 240 },     // size(60) × 4
        triangle: { width: 120, height: 208 },    // triWidth × triHeight×2 (보타이)
        sine: { width: 600, height: 600 },        // wavelength × 2
        hexagon: { width: 156, height: 90 },      // colSpacing(78) × 2, rowSpacing(90) × 1
        grid: { width: 120, height: 120 }         // cellSize × 2
    };

    private constructor() {}

    static getInstance(): SpeedLineTextureCache {
        if (!SpeedLineTextureCache.instance) {
            SpeedLineTextureCache.instance = new SpeedLineTextureCache();
        }
        return SpeedLineTextureCache.instance;
    }

    /**
     * 초기화 및 모든 패턴 텍스쳐 생성
     */
    initialize(app: Application): void {
        if (this.initialized) return;

        this.app = app;

        const patterns: BackgroundStyle[] = ['stripe', 'diamond', 'triangle', 'sine', 'hexagon', 'grid'];

        for (const pattern of patterns) {
            this.createPatternTexture(pattern, true);   // landscape
            this.createPatternTexture(pattern, false);  // portrait
        }

        this.initialized = true;
    }

    /**
     * 패턴 텍스쳐 생성
     */
    private createPatternTexture(pattern: BackgroundStyle, isLandscape: boolean): void {
        if (!this.app) return;

        const key = this.getCacheKey(pattern, isLandscape);
        if (this.textures.has(key)) return;

        const size = this.TILE_SIZES[pattern];
        const g = new Graphics();

        switch (pattern) {
            case 'stripe':
                this.drawStripePattern(g, size.width, size.height, isLandscape);
                break;
            case 'diamond':
                this.drawDiamondPattern(g, size.width, size.height);
                break;
            case 'triangle':
                this.drawTrianglePattern(g, size.width, size.height);
                break;
            case 'sine':
                this.drawSinePattern(g, size.width, size.height, isLandscape);
                break;
            case 'hexagon':
                this.drawHexagonPattern(g, size.width, size.height);
                break;
            case 'grid':
                this.drawGridPattern(g, size.width, size.height);
                break;
        }

        const renderTexture = RenderTexture.create({
            width: size.width,
            height: size.height,
            resolution: 1  // iOS 최적화
        });

        // v7 API
        this.app.renderer.render(g, { renderTexture });

        g.destroy();

        this.textures.set(key, renderTexture);
    }

    /**
     * 캐시 키 생성
     */
    private getCacheKey(pattern: BackgroundStyle, isLandscape: boolean): string {
        return `${pattern}_${isLandscape ? 'landscape' : 'portrait'}`;
    }

    /**
     * Stripe 패턴 그리기
     */
    private drawStripePattern(g: Graphics, width: number, height: number, isLandscape: boolean): void {
        const lineSize = CONFIG.GAME_SCREEN.SPEED_LINE_SIZE;
        const lineSpacing = CONFIG.GAME_SCREEN.SPEED_LINE_SPACING;

        g.beginFill(0xFFFFFF, 0.01);

        if (isLandscape) {
            // 세로 줄 (왼쪽으로 스크롤)
            for (let x = 0; x < width; x += lineSpacing) {
                g.drawRect(x, 0, lineSize, height);
            }
        } else {
            // 가로 줄 (아래로 스크롤)
            for (let y = 0; y < height; y += lineSpacing) {
                g.drawRect(0, y, width, lineSize);
            }
        }

        g.endFill();
    }

    /**
     * Diamond 패턴 그리기
     */
    private drawDiamondPattern(g: Graphics, width: number, height: number): void {
        const size = 60;

        g.beginFill(0xFFFFFF, 0.01);

        // 타일이 매끄럽게 이어지도록 그리기
        const cx = width / 2;
        const cy = height / 2;

        for (let row = -2; row <= 2; row++) {
            for (let col = -2; col <= 2; col++) {
                if ((row + col) % 2 === 0) {
                    const x = (col - row) * size + cx;
                    const y = (col + row) * size + cy;

                    g.drawPolygon([
                        x, y - size,
                        x + size, y,
                        x, y + size,
                        x - size, y
                    ]);
                }
            }
        }

        g.endFill();
    }

    /**
     * Triangle 패턴 그리기 (보타이/모래시계 타일)
     * 타일 크기: 120 x 208 (triWidth × triHeight×2)
     * triHeight = 104
     */
    private drawTrianglePattern(g: Graphics, _width: number, _height: number): void {
        const triWidth = 120;
        const triHeight = 104;

        g.beginFill(0xFFFFFF, 0.01);

        // 위쪽 반: 왼쪽 반삼각형
        g.drawPolygon([
            0, 0,
            triWidth / 2, triHeight,
            0, triHeight
        ]);

        // 위쪽 반: 오른쪽 반삼각형
        g.drawPolygon([
            triWidth, 0,
            triWidth / 2, triHeight,
            triWidth, triHeight
        ]);

        // 아래쪽 반: 완전한 삼각형 (위 방향)
        g.drawPolygon([
            0, triHeight * 2,
            triWidth / 2, triHeight,
            triWidth, triHeight * 2
        ]);

        g.endFill();
    }

    /**
     * Sine 패턴 그리기
     */
    private drawSinePattern(g: Graphics, width: number, height: number, isLandscape: boolean): void {
        const lineSpacing = 60;
        const lineWidth = 30;
        const amplitude = 40;
        const wavelength = 300;

        g.lineStyle(lineWidth, 0xFFFFFF, 0.01);

        if (isLandscape) {
            // 세로 사인파 (왼쪽으로 스크롤)
            const numLines = Math.ceil(width / lineSpacing) + 1;

            for (let i = 0; i < numLines; i++) {
                const baseX = i * lineSpacing;

                g.moveTo(baseX + Math.sin(0) * amplitude, 0);

                for (let y = 0; y <= height; y += 5) {
                    const x = baseX + Math.sin((y / wavelength) * Math.PI * 2) * amplitude;
                    g.lineTo(x, y);
                }
            }
        } else {
            // 가로 사인파 (아래로 스크롤)
            const numLines = Math.ceil(height / lineSpacing) + 1;

            for (let i = 0; i < numLines; i++) {
                const baseY = i * lineSpacing;

                g.moveTo(0, baseY + Math.sin(0) * amplitude);

                for (let x = 0; x <= width; x += 5) {
                    const y = baseY + Math.sin((x / wavelength) * Math.PI * 2) * amplitude;
                    g.lineTo(x, y);
                }
            }
        }
    }

    /**
     * Hexagon 패턴 그리기
     *
     * 타일 크기: 156 x 90 (colSpacing×2, rowSpacing×1)
     *
     * 헥사곤 중심 배치:
     * - col=0: (52, 0)   ← 짝수 열, y=0
     * - col=1: (130, 45) ← 홀수 열, y=rowSpacing/2
     *
     * 타일 반복 시:
     * - 오른쪽 타일의 col=0은 x=52+156=208 (이전 타일과 겹치지 않음)
     * - 아래 타일의 row=0은 y=0+90=90 (이전 타일 row=0 + rowSpacing)
     */
    private drawHexagonPattern(g: Graphics, _width: number, _height: number): void {
        const r = 52;
        const colSpacing = r * 1.5;  // 78
        const rowSpacing = 90;
        const hexHeight = r * 0.866; // 약 45

        const cx0 = r;           // 52
        const cy0 = 0;
        const cx1 = r + colSpacing;  // 130
        const cy1 = rowSpacing * 0.5;  // 45
        const cxL = -r * 0.5;  // -26
        const cyL = rowSpacing * 0.5;  // 45

        // 대각선 (사선) 먼저 그리기
        g.lineStyle(2, 0xFFFFFF, 0.02);

        // === 중앙 헥사곤 (52, 0) ===
        // 변 0-1 (오른쪽 → 오른쪽 아래)
        g.moveTo(cx0 + r, cy0);
        g.lineTo(cx0 + r * 0.5, cy0 + hexHeight);
        // 변 2-3 (왼쪽 아래 → 왼쪽)
        g.moveTo(cx0 - r * 0.5, cy0 + hexHeight);
        g.lineTo(cx0 - r, cy0);

        // === 오른쪽 헥사곤 (130, 45) ===
        // 변 2-3 (왼쪽 아래 → 왼쪽)
        g.moveTo(cx1 - r * 0.5, cy1 + hexHeight);
        g.lineTo(cx1 - r, cy1);
        // 변 3-4 (왼쪽 → 왼쪽 위)
        g.moveTo(cx1 - r, cy1);
        g.lineTo(cx1 - r * 0.5, cy1 - hexHeight);

        // === 왼쪽 경계 헥사곤 (-26, 45) ===
        // 변 5-0 (오른쪽 위 → 오른쪽)
        g.moveTo(cxL + r * 0.5, cyL - hexHeight);
        g.lineTo(cxL + r, cyL);
        // 변 0-1 (오른쪽 → 오른쪽 아래)
        g.moveTo(cxL + r, cyL);
        g.lineTo(cxL + r * 0.5, cyL + hexHeight);

        // 가로선 (수평) 더 두껍게 그리기
        g.lineStyle(4, 0xFFFFFF, 0.02);

        // === 중앙 헥사곤 (52, 0) ===
        // 변 1-2 (오른쪽 아래 → 왼쪽 아래) - 수평
        g.moveTo(cx0 + r * 0.5, cy0 + hexHeight);
        g.lineTo(cx0 - r * 0.5, cy0 + hexHeight);

        // === 오른쪽 헥사곤 (130, 45) ===
        // 변 4-5 (왼쪽 위 → 오른쪽 위) - 수평
        g.moveTo(cx1 - r * 0.5, cy1 - hexHeight);
        g.lineTo(cx1 + r * 0.5, cy1 - hexHeight);
        // 변 1-2 (오른쪽 아래 → 왼쪽 아래) - 수평
        g.moveTo(cx1 + r * 0.5, cy1 + hexHeight);
        g.lineTo(cx1 - r * 0.5, cy1 + hexHeight);

        // === 왼쪽 경계 헥사곤 (-26, 45) ===
        // 변 4-5 (왼쪽 위 → 오른쪽 위) - 수평
        g.moveTo(cxL - r * 0.5, cyL - hexHeight);
        g.lineTo(cxL + r * 0.5, cyL - hexHeight);
    }

    /**
     * Grid 패턴 그리기
     */
    private drawGridPattern(g: Graphics, width: number, height: number): void {
        const cellSize = 60;

        g.lineStyle(1, 0xFFFFFF, 0.04);

        // 세로선
        for (let x = 0; x <= width; x += cellSize) {
            g.moveTo(x, 0);
            g.lineTo(x, height);
        }

        // 가로선
        for (let y = 0; y <= height; y += cellSize) {
            g.moveTo(0, y);
            g.lineTo(width, y);
        }
    }

    /**
     * 텍스쳐 가져오기
     */
    getTexture(pattern: BackgroundStyle, isLandscape: boolean): Texture | null {
        const key = this.getCacheKey(pattern, isLandscape);
        return this.textures.get(key) || null;
    }

    /**
     * TilingSprite 생성
     */
    createTilingSprite(pattern: BackgroundStyle, isLandscape: boolean, width: number, height: number): TilingSprite | null {
        const texture = this.getTexture(pattern, isLandscape);
        if (!texture) return null;

        // v7 API: TilingSprite(texture, width, height)
        const sprite = new TilingSprite(texture, width, height);

        return sprite;
    }

    /**
     * 타일 크기 가져오기
     */
    getTileSize(pattern: BackgroundStyle): { width: number; height: number } {
        return this.TILE_SIZES[pattern];
    }

    /**
     * 초기화 여부
     */
    isInitialized(): boolean {
        return this.initialized;
    }

    /**
     * 정리
     */
    destroy(): void {
        for (const texture of this.textures.values()) {
            texture.destroy(true);
        }
        this.textures.clear();
        this.initialized = false;
        this.app = null;
        SpeedLineTextureCache.instance = null;
    }
}

// 글로벌 인스턴스
export const speedLineTextureCache = SpeedLineTextureCache.getInstance();
